import { INSIDER_PLAN_ID } from "./constants";

/**
 * The role permissions carried inside an access token.
 *
 * Read, not verified. Nothing here is a security decision: the API verifies
 * this same token's signature and runs the real entitlement check on every
 * request, and this only decides whether to show a fan a control or a dialog.
 * A forged token would buy somebody a button that then fails upstream.
 *
 * Returns an empty list for anything unreadable, which reads as "no special
 * permissions" and falls back to the plan check, rather than throwing inside a
 * hook that renders the whole panel.
 */
export function permissionsFromToken(token: string | undefined): string[] {
  if (typeof token !== "string") return [];
  const payload = token.split(".")[1];
  if (payload === undefined || payload.length === 0) return [];

  try {
    // base64url, which is what a JWT uses: - and _ stand in for + and /, and
    // the padding is dropped. atob accepts neither.
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { UserRole?: { Permissions?: unknown } };
    const list = claims?.UserRole?.Permissions;
    return Array.isArray(list) ? list.filter((p): p is string => typeof p === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Whether this fan may download, as far as the browser can tell.
 *
 * A COURTESY, NOT THE GATE. The tenant backend runs the real check on every
 * request, against the same claim and the same plan, and refuses with a
 * sentence written for a fan. This exists so a control that could only ever be
 * refused is not offered in the first place.
 *
 * Read from the token rather than from the signed-in user in Redux, for two
 * reasons. It is authoritative: it is the same claim the API verifies. And it
 * is there as soon as somebody is signed in, whereas the profile fetch resolves
 * later, which would hide the control from a fan who has every right to it for
 * the first moments of every page load.
 *
 * Lifted out of the Watch page's own hook because the Studio page needs the
 * same rule and cannot import from src/Pages: src/v2/__tests__/boundaries.test.ts
 * bans it, and this repo's convention is that logic both halves share lives in
 * src/utils.
 */
export function canDownloadFrom(args: {
  token: string | undefined;
  subscription?: { plan_type_id?: number | string | null } | null;
}): boolean {
  if (permissionsFromToken(args.token).includes("download")) return true;
  const planId = Number(args.subscription?.plan_type_id);
  return Number.isFinite(planId) && planId === INSIDER_PLAN_ID;
}
